import { Link } from "react-router-dom";
import Seo from "@/lib/Seo";
import { useFetch } from "@/lib/api";
import { FadeUp } from "@/lib/motion";
import Breadcrumbs from "@/components/Breadcrumbs";

export default function Collections() {
  const { data: collections, loading, error } = useFetch("/collections");

  return (
    <div className="pt-[72px]">
      <Seo
        title="Curated Wedding Collections — Coordinated Details | Marble & Veil"
        description="Collection concepts that gather welcome details, monogrammed goods, and full tablescapes — starting points we refine together, priced in your proposal."
        path="/collections"
      />
      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-24">
        <Breadcrumbs trail={[["Home", "/"], ["Collections"]]} />
        <FadeUp>
          <h1 className="mt-6 font-display text-5xl leading-[1.05] text-ink sm:text-6xl">Curated Collections</h1>
          <p className="mt-5 max-w-xl leading-relaxed text-smoke">
            Customizable collection concepts with possible elements — not guaranteed packages. Names,
            contents, and images evolve; the plan we confirm together is what counts.
          </p>
        </FadeUp>

        {loading && <p className="mt-16 text-smoke" data-testid="collections-loading">Gathering collections…</p>}
        {error && <p className="mt-16 text-red-900" data-testid="collections-error" role="alert">Collections could not be loaded just now. Please refresh in a moment.</p>}

        <div className="mt-16 space-y-20 lg:space-y-28">
          {(collections || []).map((c, i) => (
            <FadeUp key={c.slug}>
              <div className={`grid items-center gap-8 lg:grid-cols-2 lg:gap-16 ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={`collection-row-${c.slug}`}>
                <Link to={`/collections/${c.slug}`} className="block overflow-hidden bg-parch">
                  <img src={c.image} alt={`${c.name} collection concept — design inspiration`} className="aspect-[4/3] w-full object-cover transition-transform duration-[1200ms] ease-out hover:scale-[1.04]" width="900" height="675" loading="lazy" />
                </Link>
                <div>
                  <p className="mv-label">Collection {String(i + 1).padStart(2, "0")}</p>
                  <h2 className="mt-3 font-display text-3xl text-ink sm:text-4xl">
                    <Link to={`/collections/${c.slug}`} className="transition-colors hover:text-olive">{c.name}</Link>
                  </h2>
                  <p className="mt-2 font-display text-xl italic text-olive">{c.short}</p>
                  <p className="mt-4 max-w-lg leading-relaxed text-smoke">{c.description}</p>
                  <Link
                    to={`/collections/${c.slug}`}
                    data-testid={`collections-view-${c.slug}`}
                    className="mt-6 inline-block border border-ink/20 px-7 py-3 text-[12px] uppercase tracking-[0.18em] text-ink transition-colors duration-300 hover:border-olive hover:bg-olive hover:text-ivory"
                  >
                    View the Collection
                  </Link>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>
    </div>
  );
}
